import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, 
  ArrowRight, 
  Users, 
  Sparkles,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface FamilyRecord {
  id: string;
  name: string;
  tempId?: string;
  isNew?: boolean;
}

interface PersonRecord {
  id: string;
  data: Record<string, any>;
  suggestedFamilyId?: string;
  assignedFamilyId?: string;
}

interface FamilyAssignmentProps {
  type: 'parents' | 'students';
  records: PersonRecord[];
  availableFamilies: FamilyRecord[];
  mappings: Record<string, string>;
  onAssignmentsComplete: (assignments: Record<string, string>) => void;
  onBack: () => void;
}

export function FamilyAssignment({
  type,
  records,
  availableFamilies,
  mappings,
  onAssignmentsComplete,
  onBack
}: FamilyAssignmentProps) {
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const recordsPerPage = 10;

  // Get the field names from mappings
  const firstNameField = Object.entries(mappings).find(([_, target]) => target === 'first_name')?.[0];
  const lastNameField = Object.entries(mappings).find(([_, target]) => target === 'last_name')?.[0];

  // AI suggestion: Match by last name
  useEffect(() => {
    const suggestions: Record<string, string> = {};
    
    records.forEach(record => {
      const lastName = record.data[lastNameField || '']?.toLowerCase();
      if (lastName) {
        const matchingFamily = availableFamilies.find(family => 
          family.name.toLowerCase().includes(lastName)
        );
        if (matchingFamily) {
          suggestions[record.id] = matchingFamily.id;
        }
      }
    });
    
    setAssignments(suggestions);
  }, [records, availableFamilies, lastNameField]);

  const getPersonName = (record: PersonRecord) => {
    const firstName = record.data[firstNameField || ''] || '';
    const lastName = record.data[lastNameField || ''] || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown';
  };

  const unassignedCount = records.filter(r => !assignments[r.id]).length;
  const totalPages = Math.ceil(records.length / recordsPerPage);
  const currentRecords = records.slice(
    currentPage * recordsPerPage,
    (currentPage + 1) * recordsPerPage
  );

  const handleAssignment = (recordId: string, familyId: string) => {
    setAssignments(prev => ({
      ...prev,
      [recordId]: familyId
    }));
  };

  const handleComplete = () => {
    onAssignmentsComplete(assignments);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Assign {type === 'parents' ? 'Parents' : 'Students'} to Families
          </h2>
          <p className="text-gray-600">
            AI has suggested matches based on last names. Review and adjust as needed.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-gray-900">{records.length}</div>
            <p className="text-sm text-gray-500">Total {type}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {records.length - unassignedCount}
            </div>
            <p className="text-sm text-gray-500">Assigned</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-yellow-600">{unassignedCount}</div>
            <p className="text-sm text-gray-500">Unassigned</p>
          </CardContent>
        </Card>
      </div>

      {/* Assignment List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Family Assignments</span>
            <div className="flex items-center space-x-2 text-sm font-normal">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span>AI suggestions applied</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {currentRecords.map((record) => (
              <div key={record.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{getPersonName(record)}</div>
                  <div className="text-sm text-gray-500">
                    {type === 'parents' ? 'Parent' : 'Student'} ID: {record.id}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Select
                    value={assignments[record.id] || ''}
                    onValueChange={(value) => handleAssignment(record.id, value)}
                  >
                    <SelectTrigger className="w-[250px]">
                      <SelectValue placeholder="Select a family..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No family assigned</SelectItem>
                      {availableFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id}>
                          {family.name}
                          {family.isNew && (
                            <Badge variant="secondary" className="ml-2">New</Badge>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {assignments[record.id] && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage === totalPages - 1}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warning for unassigned records */}
      {unassignedCount > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {unassignedCount} {type} {unassignedCount === 1 ? 'is' : 'are'} not assigned to any family. 
            They will be skipped during import unless you assign them.
          </AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {records.length - unassignedCount} of {records.length} {type} assigned to families
        </div>
        
        <div className="flex space-x-3">
          <Button variant="outline" onClick={onBack}>
            Back to Mapping
          </Button>
          <Button 
            onClick={handleComplete}
            disabled={unassignedCount === records.length}
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}